import { React, useState, useEffect, useMemo } from "react";
import { Typography, Box } from "@mui/material";
import RDataGrid from "../reusables/RDataGrid";
import PageLoader from "../loader/PageLoader";
import useAxiosPrivate from "../hooks/useAxiosPrivate";
import useAuth from "../hooks/useAuth";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/DeleteOutlined";
import AddPopup from "../popup/AddPopup";
import Add from "@mui/icons-material/Add";
import Button from "@mui/joy/Button";
import { GridActionsCellItem } from "@mui/x-data-grid";
import Notification from "../popup/Notification";
import ConfirmDialog from "../dialog/ConfirmDialog";
import { LightTooltip } from "../tooltip/ToolTip";
import AddExpense from "../forms/AddExpense";

const Expenses = () => {
  const [isFetchingExpenses, setIsFetchingExpenses] = useState(true);
  const [expenses, setExpenses] = useState([]);
  const [editExpense, setEditExpense] = useState(false);
  const [viewExpense, setViewExpense] = useState(false);
  const [selectedExpense, setSelectedExpense] = useState(null);
  const [openPopup, setOpenPopup] = useState(false);
  const [refreshExpenses, setSetRefreshExpenses] = useState(null);

  const EXPENSES_URL = "api/v1/expenses/paged-all-by-tenant?tenantId=";
  const axiosPrivate = useAxiosPrivate();
  const { auth } = useAuth();

  const [confirmDialog, setConfirmDialog] = useState({
    isOpen: false,
    title: "",
    subTitle: "",
  });
  const [notify, setNotify] = useState({
    isOpen: false,
    message: "",
    type: "",
  });

  const divStyle = {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
  };

  // Delete expense
  const onDelete = async (id) => {
    const controller = new AbortController();
    setConfirmDialog({ ...confirmDialog, isOpen: false });

    try {
      const WORKERS_DELETE_URL = "api/v1/expenses/";
      const response = await axiosPrivate.delete(WORKERS_DELETE_URL + id, {
        signal: controller.signal,
      });
      var message = response.data.message;
      setNotify({
        isOpen: true,
        message: message,
        type: "success",
      });
      setSetRefreshExpenses(new Date());
    } catch (error) {
      var errorMessage = "";
      if (error.response) {
        // The client was given an error response (5xx, 4xx)
        const errorData = error.response.data;
        errorMessage = errorData.message;
      } else if (error.request) {
        // The client never received a response, and the request was never left
        errorMessage = "No server response";
      } else {
        // Anything else
        errorMessage = "Unexpected error occurred";
      }
      setConfirmDialog({
        isOpen: true,
        title: "Failed To Delete Expense",
        subTitle: errorMessage,
        action: "Retry",
        onConfirm: () => {
          onDelete(id);
        },
      });
    }
  };

  useEffect(() => {
    if (!isFetchingExpenses) {
      setIsFetchingExpenses(true);
    }

    let isMounted = true;
    const controller = new AbortController();

    const getExpenses = async () => {
      try {
        const response = await axiosPrivate.get(
          `${EXPENSES_URL}${auth.tenantId}&page=0&size=10`,
          {
            signal: controller.signal,
          },
          {
            headers: {
              "Content-Type": "application/json",
            },
          }
        );
        isMounted && setExpenses(response.data.data.expenses);
        setIsFetchingExpenses(false);
      } catch (error) {
        var errorMessage = "";
        if (error.response) {
          // The client was given an error response (5xx, 4xx)
          const errorData = error.response.data;
          errorMessage = errorData.message;
        } else if (error.request) {
          // The client never received a response, and the request was never left
          errorMessage = "No server response";
        } else {
          // Anything else
          errorMessage = "Unexpected error occurred";
        }
        console.log(errorMessage);
        setIsFetchingExpenses(false);
      }
    };

    getExpenses();

    return () => {
      isMounted = false;
      controller.abort();
    };

    /* eslint-disable react-hooks/exhaustive-deps */
  }, [refreshExpenses]);

  const columns = useMemo(
    () => [
      {
        field: "rowNo", // from DataGrid rows defifnition
        headerName: "No.",
        sortable: false,
        headerClassName: "super-app-theme--header",
        minWidth: 87,
        flex: 1,
        align: "left",
        headerAlign: "left",
        editable: false,
      },
      {
        field: "description",
        headerName: "Description",
        headerClassName: "super-app-theme--header",
        minWidth: 120,
        flex: 1,
        align: "left",
        headerAlign: "left",
        editable: false,
      },
      {
        field: "amount",
        headerName: "Amount",
        headerClassName: "super-app-theme--header",
        minWidth: 120,
        flex: 1,
        align: "left",
        headerAlign: "left",
        editable: false,
        renderCell: function (params) {
          return `Ksh.${params.row.amount}`;
        },
      },
      {
        field: "type",
        headerName: "Type",
        type: "boolean",
        headerClassName: "super-app-theme--header",
        minWidth: 120,
        flex: 1,
        align: "left",
        headerAlign: "left",
        editable: false,
        renderCell: function (params) {
          const type = params.row.type;
          return type === 1
            ? "Daily"
            : type === 2
            ? "Weekly"
            : type === 3
            ? "Monthly"
            : "Yearly";
        },
      },
      {
        field: "dateCreated",
        headerName: "Date Created",
        headerClassName: "super-app-theme--header",
        minWidth: 200,
        flex: 1,
        align: "left",
        headerAlign: "left",
        editable: false,
      },
      {
        field: "actions",
        type: "actions",
        headerName: "Actions",
        headerClassName: "super-app-theme--header",
        align: "center",
        headerAlign: "center",
        minWidth: 80,
        flex: 1,
        sortable: false,
        disableClickEventBubbling: true,
        cellClassName: "actions",
        getActions: (params) => {
          return [
            <LightTooltip title="Edit expense">
              <GridActionsCellItem
                icon={<EditIcon />}
                label="Edit"
                className="textPrimary"
                onClick={function () {
                  const currentRow = params.row;
                  const selectedExpense = JSON.stringify(currentRow, null, 4);
                  setViewExpense(false);
                  setEditExpense(true);
                  setSelectedExpense(selectedExpense);
                  return setOpenPopup(true);
                }}
                color="success"
              />
            </LightTooltip>,
            <LightTooltip title="Delete expense">
              <GridActionsCellItem
                icon={<DeleteIcon />}
                label="Delete"
                color="error"
                onClick={function () {
                  setConfirmDialog({
                    isOpen: true,
                    title: "Are you sure you want to delete this expense?",
                    subTitle: "You can't undo this operation",
                    action: "Delete",
                    onConfirm: () => {
                      onDelete(params.row.id);
                    },
                  });
                }}
              />
            </LightTooltip>,
          ];
        },
      },
    ],
    []
  );

  // Close dialog upon successful expense add/update
  const closeDialog = (message) => {
    setOpenPopup(false);
    setNotify({
      isOpen: true,
      message: message,
      type: "success",
    });
    setSetRefreshExpenses(new Date());
  };

  return isFetchingExpenses ? (
    <PageLoader title="Loading expenses..." />
  ) : (
    <Box
      sx={{
        height: "100%",
        width: "100%",
      }}
    >
      {expenses?.length ? (
        <Box
          sx={{
            height: "100%",
            width: "100%",
          }}
        >
          <div style={divStyle}>
            <Typography
              sx={{
                textAlign: "left",
                mt: 3,
                mb: 3,
                fontSize: {
                  lg: 30,
                  md: 20,
                  sm: 20,
                  xs: 19,
                },
              }}
            >
              Expenses
            </Typography>

            <Button
              sx={{
                size: { xs: "xs", sm: "xs", md: "md", lg: "lg", xl: "lg" },
              }}
              variant="solid"
              color="primary"
              onClick={function () {
                setEditExpense(false);
                setSelectedExpense(null);
                setOpenPopup(true);
              }}
              startDecorator={<Add />}
            >
              Expense
            </Button>
          </div>
          <Box
            sx={{
              width: "100%",
              margin: "0 auto 16px",
              height: 560,
              overflow: "hidden",
            }}
          >
            <RDataGrid columns={columns} rows={expenses} rowHeight={63} />
          </Box>
          <Notification notify={notify} setNotify={setNotify} />
          <ConfirmDialog
            confirmDialog={confirmDialog}
            setConfirmDialog={setConfirmDialog}
          />
          <AddPopup
            width="md"
            openPopup={openPopup}
            setOpenPopup={setOpenPopup}
            title={
              viewExpense
                ? "View (Edit) Expense"
                : editExpense
                ? "Edit (View) Expense"
                : "Add Expense"
            }
          >
            <AddExpense
              closeDialog={closeDialog}
              editExpense={editExpense}
              selectedExpense={selectedExpense}
            />
          </AddPopup>
        </Box>
      ) : (
        <div style={divStyle}>
          <Typography
            variant="h4"
            component="h4"
            sx={{ textAlign: "center", mt: 3, mb: 3 }}
          >
            No expenses to display
          </Typography>
          <Button
            sx={{
              size: { xs: "xs", sm: "xs", md: "md", lg: "lg", xl: "lg" },
            }}
            variant="solid"
            color="primary"
            onClick={function () {
              setEditExpense(false);
              setSelectedExpense(null);
              setOpenPopup(true);
            }}
            startDecorator={<Add />}
          >
            Expense
          </Button>
          <Notification notify={notify} setNotify={setNotify} />
          <ConfirmDialog
            confirmDialog={confirmDialog}
            setConfirmDialog={setConfirmDialog}
          />
          <AddPopup
            width="md"
            openPopup={openPopup}
            setOpenPopup={setOpenPopup}
            title={
              viewExpense
                ? "View (Edit) Expense"
                : editExpense
                ? "Edit (View) Expense"
                : "Add Expense"
            }
          >
            <AddExpense
              closeDialog={closeDialog}
              editExpense={editExpense}
              selectedExpense={selectedExpense}
            />
          </AddPopup>
        </div>
      )}
    </Box>
  );
};

export default Expenses;
